/* ==========================================================================
   Outlet4IT — Product detail page
   Web Components: <o4-gallery>, <o4-product>
   No dependencies. ES6.
   ========================================================================== */
(function () {
  'use strict';

  var money = function (cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.Shopify.money_format);
    }
    return (cents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2 }) + ' €';
  };

  /* ────────────────────────────────────────────────────────────────────────
     Gallery — thumbnails, mobile swipe sync, desktop hover-zoom, lightbox
  ──────────────────────────────────────────────────────────────────────── */
  class O4Gallery extends HTMLElement {
    connectedCallback() {
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-thumb]'));
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-slide]'));
      this.dots   = Array.prototype.slice.call(this.querySelectorAll('[data-dot]'));
      this.track  = this.querySelector('[data-track]');
      this.stage  = this.querySelector('[data-stage]');
      this.lightbox = this.querySelector('[data-lightbox]');
      this.lightboxImg = this.querySelector('[data-lightbox-img]');
      this.index = 0;
      this.desktop = window.matchMedia('(min-width: 750px)');

      this.thumbs.forEach(function (t) {
        t.addEventListener('click', function () { this.show(parseInt(t.dataset.thumb, 10)); }.bind(this));
      }.bind(this));

      if (this.track) {
        var self = this, raf;
        this.track.addEventListener('scroll', function () {
          if (self.desktop.matches) return;
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(function () {
            var w = self.track.clientWidth;
            if (w) self._setActive(Math.round(self.track.scrollLeft / w), false);
          });
        }, { passive: true });
      }

      this._bindZoom();
      this._bindLightbox();
    }

    show(i) {
      this._setActive(i, true);
      if (!this.desktop.matches && this.track) {
        var slide = this.slides[i];
        if (slide) this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      }
    }

    _setActive(i, updateThumbScroll) {
      if (i < 0 || i >= this.slides.length) return;
      this.index = i;
      this.slides.forEach(function (s, n) { s.classList.toggle('is-active', n === i); });
      this.thumbs.forEach(function (t, n) {
        var on = n === i;
        t.classList.toggle('is-active', on);
        if (on && updateThumbScroll) t.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
      this.dots.forEach(function (d, n) { d.classList.toggle('is-active', n === i); });
    }

    _bindZoom() {
      if (!this.stage) return;
      var self = this;
      this.stage.addEventListener('mousemove', function (e) {
        if (!self.desktop.matches) return;
        var img = self.slides[self.index] && self.slides[self.index].querySelector('img');
        if (!img) return;
        var r = self.stage.getBoundingClientRect();
        img.style.transformOrigin =
          ((e.clientX - r.left) / r.width * 100) + '% ' + ((e.clientY - r.top) / r.height * 100) + '%';
      });
      this.stage.addEventListener('mouseenter', function () {
        if (self.desktop.matches) self.stage.classList.add('is-zooming');
      });
      this.stage.addEventListener('mouseleave', function () {
        self.stage.classList.remove('is-zooming');
      });
    }

    _bindLightbox() {
      if (!this.lightbox) return;
      var self = this;
      this.slides.forEach(function (slide) {
        slide.addEventListener('click', function () {
          if (!self.desktop.matches) return; /* mobile keeps native pinch zoom */
          var src = slide.dataset.zoom;
          var img = slide.querySelector('img');
          if (!src) return;
          self.lightboxImg.src = src;
          self.lightboxImg.alt = img ? img.alt : '';
          self.lightbox.classList.add('is-open');
          document.body.style.overflow = 'hidden';
        });
      });
      this.lightbox.querySelectorAll('[data-lightbox-close]').forEach(function (b) {
        b.addEventListener('click', self._closeLightbox.bind(self));
      });
      this.lightbox.addEventListener('click', function (e) {
        if (e.target === self.lightbox) self._closeLightbox();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') self._closeLightbox();
      });
    }

    _closeLightbox() {
      if (!this.lightbox) return;
      this.lightbox.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  }
  customElements.define('o4-gallery', O4Gallery);

  /* ────────────────────────────────────────────────────────────────────────
     Product — qty, variants, sticky bar, cart AJAX, wishlist/compare
  ──────────────────────────────────────────────────────────────────────── */
  class O4Product extends HTMLElement {
    connectedCallback() {
      this.gallery   = this.querySelector('o4-gallery');
      this.form      = this.querySelector('form[action$="/cart/add"], form.o4-buy');
      this.variantInput = this.querySelector('[data-variant-id]');
      this.qtyInput  = this.querySelector('[data-qty-input]');
      this.atc       = this.querySelector('[data-atc]');
      this.atcLabel  = this.querySelector('[data-atc-label]');
      this.feedback  = this.querySelector('[data-atc-feedback]');
      this.stickybar = this.querySelector('[data-stickybar]');
      this.lowStock  = parseInt(this.dataset.lowStock || '5', 10);
      this.i18n = {
        inStock:     this.dataset.i18nInStock || 'In stock',
        outStock:    this.dataset.i18nOutStock || 'Unavailable',
        addToCart:   this.dataset.i18nAtc || 'Add to cart',
        unavailable: this.dataset.i18nUnavailable || 'Unavailable'
      };
      this.variants  = this._parseVariants();
      this.current   = this.variants[0] || null;

      this._bindQty();
      this._bindVariants();
      this._bindForm();
      this._bindStickybar();
      this._bindWishlistCompare();
    }

    _parseVariants() {
      var el = this.querySelector('[data-product-json]');
      if (!el) return [];
      try { return JSON.parse(el.textContent); } catch (e) { return []; }
    }

    /* Quantity */
    _bindQty() {
      var minus = this.querySelector('[data-qty-minus]');
      var plus  = this.querySelector('[data-qty-plus]');
      var self = this;
      function step(d) {
        if (!self.qtyInput) return;
        var val = Math.max(1, (parseInt(self.qtyInput.value, 10) || 1) + d);
        self.qtyInput.value = val;
      }
      if (minus) minus.addEventListener('click', function () { step(-1); });
      if (plus) plus.addEventListener('click', function () { step(1); });
    }

    /* Variants */
    _bindVariants() {
      var groups = Array.prototype.slice.call(this.querySelectorAll('[data-option-index]'));
      if (!groups.length) return;
      var self = this;
      this.addEventListener('change', function (e) {
        if (!e.target.matches('[data-option-value]')) return;
        var selected = groups.map(function (g) {
          var c = g.querySelector('input:checked');
          return c ? c.value : null;
        });
        var match = self.variants.find(function (v) {
          return v.options.every(function (opt, i) { return opt === selected[i]; });
        });
        if (match) self._applyVariant(match);
      });
    }

    _applyVariant(v) {
      this.current = v;
      if (this.variantInput) {
        this.variantInput.value = v.id;
        this.variantInput.disabled = !v.available;
      }
      var priceEl = this.querySelector('[data-price]');
      if (priceEl) priceEl.textContent = money(v.price);
      var stickyPrice = this.querySelector('[data-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = money(v.price);

      var compareEl = this.querySelector('[data-compare]');
      var saveEl = this.querySelector('[data-save]');
      if (v.compare_at_price && v.compare_at_price > v.price) {
        if (compareEl) compareEl.textContent = money(v.compare_at_price);
        if (saveEl) {
          var pct = Math.round((v.compare_at_price - v.price) * 100 / v.compare_at_price);
          saveEl.lastChild.textContent = ' −' + pct + '%';
        }
      } else {
        if (compareEl) compareEl.textContent = '';
        if (saveEl) saveEl.textContent = '';
      }
      this._updateAvailability(v);
    }

    _updateAvailability(v) {
      var wrap = this.querySelector('[data-avail]');
      var label = this.querySelector('[data-avail-label]');
      if (wrap) wrap.className = 'o4-avail ' + (v.available ? 'o4-avail--in' : 'o4-avail--out');
      if (label) label.textContent = v.available ? this.i18n.inStock : this.i18n.outStock;
      if (this.atc) {
        this.atc.disabled = !v.available;
        if (this.atcLabel) this.atcLabel.textContent = v.available ? this.i18n.addToCart : this.i18n.unavailable;
      }
      var stickyBtn = this.querySelector('[data-sticky-atc]');
      if (stickyBtn) stickyBtn.disabled = !v.available;
    }

    /* Cart AJAX */
    _bindForm() {
      if (!this.form) return;
      var self = this;
      this.form.addEventListener('submit', function (e) {
        e.preventDefault();
        self._addToCart();
      });
    }

    _addToCart() {
      if (this._busy || !this.form) return;
      this._busy = true;
      if (this.atc) this.atc.classList.add('o4-btn--loading');
      var data = new FormData(this.form);
      fetch('/cart/add.js', {
        method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest' }, body: data
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error(res.body && res.body.description || 'Cart error');
          this._showFeedback();
          this._updateCartCount();
          document.dispatchEvent(new CustomEvent('cart:item-added', { bubbles: true, detail: res.body }));
        }.bind(this))
        .catch(function (err) { console.error('[O4Product]', err); })
        .then(function () {
          this._busy = false;
          if (this.atc) this.atc.classList.remove('o4-btn--loading');
        }.bind(this));
    }

    _showFeedback() {
      if (!this.feedback) return;
      this.feedback.classList.add('is-visible');
      clearTimeout(this._fbT);
      this._fbT = setTimeout(function () { this.feedback.classList.remove('is-visible'); }.bind(this), 4000);
    }

    _updateCartCount() {
      fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          document.querySelectorAll('.o4-header__cart-count').forEach(function (el) {
            el.textContent = cart.item_count;
            el.style.display = cart.item_count > 0 ? '' : 'none';
          });
          /* If a count badge doesn't exist yet, create it */
          var cartLink = document.querySelector('.o4-header__cart');
          if (cartLink && !cartLink.querySelector('.o4-header__cart-count') && cart.item_count > 0) {
            var span = document.createElement('span');
            span.className = 'o4-header__cart-count';
            span.textContent = cart.item_count;
            cartLink.appendChild(span);
          }
        });
    }

    /* Sticky bar (mobile) */
    _bindStickybar() {
      if (!this.stickybar || !this.atc) return;
      var self = this;
      var stickyBtn = this.querySelector('[data-sticky-atc]');
      if (stickyBtn) stickyBtn.addEventListener('click', function () { self.atc.click(); });

      if (!('IntersectionObserver' in window)) return;
      this._obs = new IntersectionObserver(function (entries) {
        /* Show bar once the main ATC has scrolled out of view */
        self.stickybar.classList.toggle('is-visible', !entries[0].isIntersecting);
        self.stickybar.setAttribute('aria-hidden', entries[0].isIntersecting ? 'true' : 'false');
      }, { rootMargin: '0px 0px -10% 0px' });
      this._obs.observe(this.atc);
    }

    /* Wishlist + compare (localStorage) */
    _bindWishlistCompare() {
      this._toggleStore('[data-wishlist]', 'o4_wishlist');
      this._toggleStore('[data-compare]', 'o4_compare');
    }

    _toggleStore(selector, key) {
      var btn = this.querySelector(selector);
      if (!btn) return;
      var handle = btn.dataset.handle;
      var list = this._read(key);
      if (list.indexOf(handle) !== -1) {
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
      }
      btn.addEventListener('click', function () {
        var arr = this._read(key);
        var i = arr.indexOf(handle);
        if (i === -1) { arr.push(handle); btn.classList.add('is-active'); btn.setAttribute('aria-pressed', 'true'); }
        else { arr.splice(i, 1); btn.classList.remove('is-active'); btn.setAttribute('aria-pressed', 'false'); }
        try { localStorage.setItem(key, JSON.stringify(arr)); } catch (e) {}
      }.bind(this));
    }

    _read(key) {
      try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch (e) { return []; }
    }
  }
  customElements.define('o4-product', O4Product);
})();
